import { useState, useEffect, useCallback } from "react";
import { useDebounce } from "../../../../../../../core/utils/useDebounce";
import { searchQueueOrders } from "../../../../../../../core/services/queue/queueService";
import {
  QueueOrderSearchRequest,
  QueueOrderSearchStatus,
} from "../../../../../../../models/queue/queueOrderSearchRequest";
import { QueueOrderSearchResult } from "../../../../../../../models/queue/queueOrderSearchResponse";

interface UseOrdersSearchProps {
  queueId: string | undefined;
  orderNumbers: string[];
  selectedStatuses: QueueOrderSearchStatus[];
  actionDate: string;
  showOnlyNullActionDate: boolean;
  filterSearch: string;
}

interface UseOrdersSearchReturn {
  orders: QueueOrderSearchResult[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  sortBy: string;
  sortDirection: "asc" | "desc";
  isLoading: boolean;
  error: string | null;
  setPageNumber: (page: number) => void;
  setPageSize: (size: number) => void;
  setSortBy: (sortBy: string) => void;
  setSortDirection: (direction: "asc" | "desc") => void;
  handleSort: (column: string) => void;
  handlePageChange: (event: unknown, newPage: number) => void;
  handlePageSizeChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  searchOrders: () => Promise<void>;
  resetSearch: () => void;
}

export const useOrdersSearch = ({
  queueId,
  orderNumbers,
  selectedStatuses,
  actionDate,
  showOnlyNullActionDate,
  filterSearch,
}: UseOrdersSearchProps): UseOrdersSearchReturn => {
  // Table and pagination state
  const [orders, setOrders] = useState<QueueOrderSearchResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1); // 1-based pagination
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<string>("OrderNo");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Loading and error state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getSelectedStatuses = useCallback(() => {
    if (selectedStatuses.includes(QueueOrderSearchStatus.All)) {
      return undefined;
    }
    if (selectedStatuses.length > 0) {
      return selectedStatuses;
    }
    return undefined;
  }, [selectedStatuses]);

  // Search function - always uses current React state
  const searchOrders = useCallback(
    async () => {
      if (!queueId) return;

      setIsLoading(true);
      setError(null);

      try {
        const searchRequest: QueueOrderSearchRequest = {
          page: pageNumber,
          pageSize: pageSize,
          searchParameters: {
            orderNumbers: orderNumbers.length > 0 ? orderNumbers : undefined,
            statuses: getSelectedStatuses(),
            actionDate: !showOnlyNullActionDate ? (actionDate || undefined) : undefined,
            hasNoActionDate: showOnlyNullActionDate ? true : undefined,
            filterSearch: filterSearch || undefined,
          },
          sortBy: sortBy,
          sortDirection: sortDirection,
        };

        const response = await searchQueueOrders(queueId, searchRequest);

        if (response) {
          setOrders(response.data);
          setTotalCount(response.totalCount);
        } else {
          setOrders([]);
          setTotalCount(0);
        }
      } catch (err) {
        setError("Failed to search orders");
        console.error("Search error:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [
      queueId,
      orderNumbers,
      selectedStatuses,
      actionDate,
      showOnlyNullActionDate,
      filterSearch,
      pageNumber,
      pageSize,
      sortBy,
      sortDirection,
    ]
  );

  // Debounced search function
  const debouncedSearch = useDebounce(searchOrders, 500);

  const handleSort = useCallback((column: string) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDirection("asc");
    }
  }, [sortBy, sortDirection]);

  const handlePageChange = useCallback((event: unknown, newPage: number) => {
    setPageNumber(newPage + 1); // TablePagination uses 0-based, we use 1-based
  }, []);

  const handlePageSizeChange = useCallback((
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPageNumber(1);
  }, []);

  const resetSearch = useCallback(() => {
    setOrders([]);
    setTotalCount(0);
    setPageNumber(1);
    setPageSize(10);
    setSortBy("OrderNo");
    setSortDirection("asc");
    setError(null);
  }, []);

  // Effect to trigger immediate search when dependencies change
  useEffect(() => {
    if (queueId) {
      searchOrders();
    }
  }, [
    queueId,
    orderNumbers,
    selectedStatuses,
    actionDate,
    showOnlyNullActionDate,
    pageNumber,
    pageSize,
    sortBy,
    sortDirection,
  ]);

  // Effect to trigger debounced search for filter search
  useEffect(() => {
    if (queueId) {
      debouncedSearch();
    }
  }, [filterSearch, queueId]);

  // Reset to first page when search criteria change (except pagination and sorting)
  useEffect(() => {
    if (pageNumber !== 1) {
      setPageNumber(1);
    }
  }, [orderNumbers, actionDate, showOnlyNullActionDate, filterSearch]);

  return {
    orders,
    totalCount,
    pageNumber,
    pageSize,
    sortBy,
    sortDirection,
    isLoading,
    error,
    setPageNumber,
    setPageSize,
    setSortBy,
    setSortDirection,
    handleSort,
    handlePageChange,
    handlePageSizeChange,
    searchOrders,
    resetSearch,
  };
};
